import { SearchType } from '@aibees/elephant/types';
import {
  Label,
  LabelGrouping,
  LabelType,
  OwnershipScope,
} from '@aibees/elephant/types/label';
import { IconType } from '@aibees/shared/components';
import { listFilterLabelParser } from '../components/filter-tags/utils';
import {
  FilterCategory,
  SearchFilter,
  SearchFilterBase,
  SearchFilterType,
} from '../types';
import { listAndLabelResponseFilterFn, listsResponseMapper } from '../utils';

export default function listsFilterConfig(
  searchType: SearchType,
  employees?: boolean
): SearchFilterBase {
  const companiesList: SearchFilter = {
    name: 'Companies',
    type: SearchFilterType.NONE,
    index: 2,
    key: 'companies_list',
    field: 'companies_list',
    metadata: {
      tabs: {
        icon: 'business',
        iconColor: 'primary',
      },
    },
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: 'Companies',
        },
      },
    },
    sections: { main: 2, more: 1 },
    open: true,
    mainItem: true,
    layer: 3,
    items: [
      {
        name: 'Include',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        layer: 4,
        field: 'companies_include_list',
        key: 'companies_include_list',
        icon: 'business',
        metadata: {
          color: 'primary',
          icon: 'add-circle-outline',
          placeholder: 'Enter Company Lists, (OR) Search',
          delimiter: '::',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
            iconSx: {
              color: 'primary.500',
            },
            tabsCountBadge: true,
            onlySearchInResults: true,
          },
        },
        utils: {
          optionsApiFormatter: (
            data?: Label[],
            icon?: IconType,
            delimiter?: string
          ) => listsResponseMapper(data, icon, delimiter),
          tags: {
            labelFormatter: listFilterLabelParser,
          },
        },
        searchKey: 'name',
        config: {
          labelKey: 'name',
          valueKey: 'value',
          secondaryIcon: 'secondaryIcon',
        },
        tabs: ['All', 'Mine', 'Team'],
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LIST,
                LabelGrouping.COMPANY,
                undefined,
                userId
              ),
            valueKey: 'labels',
            label: 'All',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LIST,
                LabelGrouping.COMPANY,
                OwnershipScope.MINE,
                userId
              ),
            valueKey: 'labels',
            label: 'Mine',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LIST,
                LabelGrouping.COMPANY,
                OwnershipScope.TEAM,
                userId
              ),
            valueKey: 'labels',
            label: 'Team',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
        ],
      },
      {
        name: 'Exclude',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 1,
        layer: 4,
        key: 'companies_exclude_list',
        field: 'companies_exclude_list',
        icon: 'business',
        metadata: {
          color: 'error',
          icon: 'block',
          placeholder: 'Enter Company Lists, (OR) Search',
          delimiter: '::',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
            iconSx: {
              color: 'primary.500',
            },
            tabsCountBadge: true,
            onlySearchInResults: true,
          },
        },
        utils: {
          optionsApiFormatter: (
            data?: Label[],
            icon?: IconType,
            delimiter?: string
          ) => listsResponseMapper(data, icon, delimiter),
          tags: {
            labelFormatter: listFilterLabelParser,
          },
        },
        searchKey: 'name',
        config: {
          labelKey: 'name',
          valueKey: 'value',
          secondaryIcon: 'secondaryIcon',
        },
        tabs: ['All', 'Mine', 'Team'],
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LIST,
                LabelGrouping.COMPANY,
                undefined,
                userId
              ),
            valueKey: 'labels',
            label: 'All',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LIST,
                LabelGrouping.COMPANY,
                OwnershipScope.MINE,
                userId
              ),
            valueKey: 'labels',
            label: 'Mine',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LIST,
                LabelGrouping.COMPANY,
                OwnershipScope.TEAM,
                userId
              ),
            valueKey: 'labels',
            label: 'Team',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
        ],
      },
      {
        name: 'Include (AND)',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 2,
        layer: 4,
        field: 'companies_all_of_list',
        key: 'companies_all_of_list',
        icon: 'business',
        metadata: {
          color: 'warning',
          icon: 'add-task',
          placeholder: 'Enter Company Lists, (AND) Search',
          delimiter: '::',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
            iconSx: {
              color: 'primary.500',
            },
            tabsCountBadge: true,
            onlySearchInResults: true,
          },
        },
        utils: {
          optionsApiFormatter: (
            data?: Label[],
            icon?: IconType,
            delimiter?: string
          ) => listsResponseMapper(data, icon, delimiter),
          tags: {
            labelFormatter: listFilterLabelParser,
          },
        },
        searchKey: 'name',
        config: {
          labelKey: 'name',
          valueKey: 'value',
          secondaryIcon: 'secondaryIcon',
        },
        tabs: ['All', 'Mine', 'Team'],
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LIST,
                LabelGrouping.COMPANY,
                undefined,
                userId
              ),
            valueKey: 'labels',
            label: 'All',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LIST,
                LabelGrouping.COMPANY,
                OwnershipScope.MINE,
                userId
              ),
            valueKey: 'labels',
            label: 'Mine',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LIST,
                LabelGrouping.COMPANY,
                OwnershipScope.TEAM,
                userId
              ),
            valueKey: 'labels',
            label: 'Team',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
        ],
      },
    ],
  };
  const peopleList: SearchFilter = {
    name: 'People',
    type: SearchFilterType.NONE,
    index: 3,
    key: 'people_list',
    field: 'people_list',
    metadata: {
      tabs: {
        icon: 'people',
        iconColor: 'success',
      },
    },
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: 'Contacts',
        },
      },
    },
    sections: { main: 2, more: 1 },
    open: true,
    mainItem: true,
    layer: 3,
    items: [
      {
        name: 'Include',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 0,
        layer: 4,
        field: 'people_include_list',
        key: 'people_include_list',
        icon: 'people',
        metadata: {
          color: 'primary',
          icon: 'add-circle-outline',
          placeholder: 'Enter People Lists, (OR) Search',
          delimiter: '::',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
            iconSx: {
              color: 'success.500',
            },
            tabsCountBadge: true,
            onlySearchInResults: true,
          },
        },
        utils: {
          optionsApiFormatter: (
            data?: Label[],
            icon?: IconType,
            delimiter?: string
          ) => listsResponseMapper(data, icon, delimiter),
          tags: {
            labelFormatter: listFilterLabelParser,
          },
        },
        searchKey: 'name',
        config: {
          labelKey: 'name',
          valueKey: 'value',
          secondaryIcon: 'secondaryIcon',
        },
        tabs: ['All', 'Mine', 'Team'],
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LIST,
                LabelGrouping.PEOPLE,
                undefined,
                userId
              ),
            valueKey: 'labels',
            label: 'All',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LIST,
                LabelGrouping.PEOPLE,
                OwnershipScope.MINE,
                userId
              ),
            valueKey: 'labels',
            label: 'Mine',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LIST,
                LabelGrouping.PEOPLE,
                OwnershipScope.TEAM,
                userId
              ),
            valueKey: 'labels',
            label: 'Team',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
        ],
      },
      {
        name: 'Exclude',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 1,
        layer: 4,
        key: 'people_exclude_list',
        field: 'people_exclude_list',
        icon: 'people',
        metadata: {
          color: 'error',
          icon: 'block',
          placeholder: 'Enter People Lists, (OR) Search',
          delimiter: '::',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
            iconSx: {
              color: 'success.500',
            },
            tabsCountBadge: true,
            onlySearchInResults: true,
          },
        },
        utils: {
          optionsApiFormatter: (
            data?: Label[],
            icon?: IconType,
            delimiter?: string
          ) => listsResponseMapper(data, icon, delimiter),
          tags: {
            labelFormatter: listFilterLabelParser,
          },
        },
        searchKey: 'name',
        config: {
          labelKey: 'name',
          valueKey: 'value',
          secondaryIcon: 'secondaryIcon',
        },
        tabs: ['All', 'Mine', 'Team'],
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LIST,
                LabelGrouping.PEOPLE,
                undefined,
                userId
              ),
            valueKey: 'labels',
            label: 'All',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LIST,
                LabelGrouping.PEOPLE,
                OwnershipScope.MINE,
                userId
              ),
            valueKey: 'labels',
            label: 'Mine',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LIST,
                LabelGrouping.PEOPLE,
                OwnershipScope.TEAM,
                userId
              ),
            valueKey: 'labels',
            label: 'Team',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
        ],
      },
      {
        name: 'Include (AND)',
        type: SearchFilterType.CUSTOM_SELECT,
        index: 2,
        layer: 4,

        field: 'people_all_of_list',
        key: 'people_all_of_list',
        icon: 'people',
        metadata: {
          color: 'warning',
          icon: 'add-task',
          placeholder: 'Enter People Lists, (AND) Search',
          delimiter: '::',
          autocomplete: {
            multiple: true,
            checkboxPosition: 'left',
            iconSx: {
              color: 'success.500',
            },
            tabsCountBadge: true,
            onlySearchInResults: true,
          },
        },
        utils: {
          optionsApiFormatter: (
            data?: Label[],
            icon?: IconType,
            delimiter?: string
          ) => listsResponseMapper(data, icon, delimiter),
          tags: {
            labelFormatter: listFilterLabelParser,
          },
        },
        searchKey: 'name',
        config: {
          labelKey: 'name',
          valueKey: 'value',
          secondaryIcon: 'secondaryIcon',
        },
        tabs: ['All', 'Mine', 'Team'],
        options: [
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LIST,
                LabelGrouping.PEOPLE,
                undefined,
                userId
              ),
            valueKey: 'labels',
            label: 'All',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LIST,
                LabelGrouping.PEOPLE,
                OwnershipScope.MINE,
                userId
              ),
            valueKey: 'labels',
            label: 'Mine',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
          {
            type: 'URL',
            value: `/elephant-management/um/workspaces/:wid/bootstrap`,
            filterFn: (item: Label, userId?: string) =>
              listAndLabelResponseFilterFn(
                item,
                LabelType.LIST,
                LabelGrouping.PEOPLE,
                OwnershipScope.TEAM,
                userId
              ),
            valueKey: 'labels',
            label: 'Team',
            icon: null,
            index: undefined,
            queryKeys: ['lists_and_labels_filter_options_query'],
          },
        ],
      },
    ],
  };

  return {
    id: 'In & Exclude Lists',
    name: 'In & Exclude Lists',
    icon: 'list-outline',
    index: 23,
    type: SearchFilterType.ROOT,
    category: [FilterCategory.RECORD_MANAGEMENT],
    pinned: true,
    items: employees
      ? [peopleList]
      : [
          {
            name: '',
            type: SearchFilterType.BUTTON_GROUP,
            icon: undefined,
            index: 0,
            key: '',
            field: '',
            metadata: {
              allowParallelItems: true,
              tabs: {
                defaultOpenIndex: searchType === SearchType.Company ? 0 : 1,
              },
            },
            config: { labelKey: 'label', valueKey: 'value' },
            items: [
              ...[
                {
                  name: 'Applied Filters',
                  type: SearchFilterType.APPLIED_FILTERS,
                  index: 2,
                  icon: undefined,
                  key: 'lists_applied_filters',
                  field: 'lists_applied_filters',
                  metadata: {},
                },
              ],
              ...[companiesList, peopleList],
            ],
          },
        ],
  };
}
